/**
 * Outbox — regras de sincronização offline.
 *
 * Deliberadamente PURO: nenhuma referência a IndexedDB, `fetch` ou `window`.
 * A persistência entra por uma interface (`OutboxStore`), e o transporte por
 * outra (`MutationSender`). É o que permite testar as regras difíceis — ordem,
 * idempotência, conflito, retry — em Node, sem navegador.
 *
 * Arquitetura aprovada (Architecture Review §7), agora sobre IndexedDB:
 *   - toda entidade criada offline recebe UUIDv7 gerado no cliente;
 *   - toda mutação leva `Idempotency-Key`, então reenviar é seguro;
 *   - conflito (409) NUNCA é descartado silenciosamente.
 *
 * Limite de plataforma que molda este módulo: o iOS não tem Background Sync.
 * O outbox só drena com o app em primeiro plano, então drenar é uma ação
 * explícita — `drainOutbox` — chamada na abertura, ao voltar a rede, e pelo
 * botão "Sincronizar agora".
 */

export type MutationStatus = 'pending' | 'sending' | 'conflict' | 'failed';

export type MutationOperation = 'create' | 'update' | 'delete';

export interface Mutation {
  /** UUIDv7 gerado no cliente. Também é a chave de idempotência. */
  mutationId: string;
  entity: string;
  operation: MutationOperation;
  payload: unknown;
  /** Versão base para bloqueio otimista. Ausente em criação. */
  baseVersion?: number;
  createdAt: number;
  retryCount: number;
  status: MutationStatus;
  /** Preenchido quando o servidor devolve 409. */
  conflict?: { serverState: unknown; message?: string };
}

export interface OutboxStore {
  /** Em ordem de criação. A ordem importa: mutações dependem das anteriores. */
  list(): Promise<Mutation[]>;
  put(mutation: Mutation): Promise<void>;
  remove(mutationId: string): Promise<void>;
}

export type SendResult =
  | { kind: 'ok'; version?: number }
  /** 409 — o servidor tem estado divergente. Precisa de decisão humana. */
  | { kind: 'conflict'; serverState: unknown; message?: string }
  /** 4xx que não vai melhorar com retry. */
  | { kind: 'rejected'; message: string }
  /** Rede ou 5xx — vale tentar de novo. */
  | { kind: 'retry'; message: string };

export type MutationSender = (mutation: Mutation) => Promise<SendResult>;

export interface DrainOutcome {
  sent: number;
  conflicts: number;
  rejected: number;
  /** Ficaram na fila para a próxima tentativa. */
  remaining: number;
  /** Parou porque a rede caiu no meio. */
  stoppedEarly: boolean;
}

/** Depois disto, para de tentar sozinho e espera ação explícita do usuário. */
export const MAX_AUTOMATIC_RETRIES = 5;

export function pendingCount(mutations: readonly Mutation[]): number {
  return mutations.filter((m) => m.status !== 'conflict').length;
}

export function conflictCount(mutations: readonly Mutation[]): number {
  return mutations.filter((m) => m.status === 'conflict').length;
}

/**
 * Drena a fila, em ordem.
 *
 * Regras que valem a pena enunciar:
 *
 * - **Ordem estrita.** Criar uma atividade e editá-la em seguida são duas
 *   mutações dependentes. Enviar fora de ordem quebraria a segunda.
 * - **Para no primeiro erro de rede.** Continuar tentando as seguintes só
 *   gasta bateria e embaralha a ordem.
 * - **Conflito não bloqueia a fila.** A mutação conflitada sai da frente e
 *   fica marcada para revisão; as demais seguem.
 * - **Nada é descartado em silêncio.** Só sai da fila o que o servidor
 *   confirmou ou rejeitou definitivamente — e a rejeição é reportada.
 */
export async function drainOutbox(
  store: OutboxStore,
  send: MutationSender,
): Promise<DrainOutcome> {
  const outcome: DrainOutcome = {
    sent: 0,
    conflicts: 0,
    rejected: 0,
    remaining: 0,
    stoppedEarly: false,
  };

  const queue = await store.list();

  for (const mutation of queue) {
    // Já em conflito: espera decisão do usuário, não se auto-reenvia.
    if (mutation.status === 'conflict') {
      outcome.conflicts += 1;
      continue;
    }

    if (mutation.retryCount >= MAX_AUTOMATIC_RETRIES) {
      outcome.remaining += 1;
      continue;
    }

    await store.put({ ...mutation, status: 'sending' });

    const result = await send(mutation);

    if (result.kind === 'ok') {
      await store.remove(mutation.mutationId);
      outcome.sent += 1;
      continue;
    }

    if (result.kind === 'conflict') {
      await store.put({
        ...mutation,
        status: 'conflict',
        conflict: {
          serverState: result.serverState,
          ...(result.message === undefined ? {} : { message: result.message }),
        },
      });
      outcome.conflicts += 1;
      continue;
    }

    if (result.kind === 'rejected') {
      // Não vai melhorar com retry. Sai da fila, mas é contabilizado — a UI
      // precisa contar o que foi perdido, e por quê.
      await store.remove(mutation.mutationId);
      outcome.rejected += 1;
      continue;
    }

    // 'retry': rede ou servidor instável. Devolve à fila e PARA.
    await store.put({
      ...mutation,
      status: 'pending',
      retryCount: mutation.retryCount + 1,
    });
    outcome.remaining += 1;
    outcome.stoppedEarly = true;
    break;
  }

  if (outcome.stoppedEarly) {
    const restantes = queue.length - outcome.sent - outcome.conflicts - outcome.rejected;
    outcome.remaining = Math.max(outcome.remaining, restantes);
  }

  return outcome;
}

/** Resolução de um conflito pelo usuário: aplicar o meu, ou aceitar o servidor. */
export async function resolveConflict(
  store: OutboxStore,
  mutationId: string,
  resolution: 'keep_server' | 'apply_mine',
): Promise<void> {
  const mutation = (await store.list()).find((m) => m.mutationId === mutationId);
  if (!mutation) return;

  if (resolution === 'keep_server') {
    await store.remove(mutationId);
    return;
  }

  // "Aplicar as minhas alterações": reenvia sobre a versão que o servidor
  // acabou de mostrar, e não sobre a base obsoleta.
  const serverVersion = (mutation.conflict?.serverState as { version?: number } | undefined)
    ?.version;

  const { conflict: _discarded, ...rest } = mutation;

  await store.put({
    ...rest,
    status: 'pending',
    retryCount: 0,
    ...(serverVersion === undefined ? {} : { baseVersion: serverVersion }),
  });
}
